
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Trophy, TrendingUp, DollarSign, Coins, ChevronUp, ChevronDown } from "lucide-react";
import { formatNumber } from "@/lib/utils";
import { useNetworkStats } from "@/hooks/useNetworkStats";
import { useNodeSelection } from "@/contexts/node-selection";
import { useState } from "react";

type SortField = 'stake' | 'total_rewards' | 'hourly_reward_amount' | 'network_percentage' | 'apy' | 'stake_value' | 'hourly_usd';
type SortDirection = 'asc' | 'desc';

export function TopStakers() {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('stake');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const stakersPerPage = 50;
  const { setSelectedNode } = useNodeSelection();

  // First get the total count of active stakers
  const { data: totalCount } = useQuery({
    queryKey: ["totalActiveStakers"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('node_rankings')
        .select('*', { count: 'exact', head: true });
      
      if (error) throw error;
      return count || 0;
    },
    refetchInterval: 60000,
  });

  const { data: allStakers, isLoading } = useQuery({
    queryKey: ["allActiveStakers", currentPage, sortField, sortDirection],
    queryFn: async () => {
      const from = (currentPage - 1) * stakersPerPage;
      const to = from + stakersPerPage - 1;

      // Get paginated stakers with dynamic sorting
      let query = supabase
        .from('node_rankings')
        .select('*');

      // Apply sorting based on current sort field
      if (sortField === 'stake' || sortField === 'total_rewards') {
        query = query.order(sortField, { ascending: sortDirection === 'asc' });
      } else {
        // For fields not directly in node_rankings, default to stake sorting
        query = query.order('stake', { ascending: false });
      }

      const { data: rankings, error: rankingsError } = await query.range(from, to);
      
      if (rankingsError) {
        console.error('Error fetching node rankings:', rankingsError);
        throw rankingsError;
      }

      // Get public_key data for these addresses
      const addresses = rankings?.map(r => r.address) || [];
      const { data: provisionersData, error: provisionersError } = await supabase
        .from('provisioners')
        .select('address, public_key')
        .in('address', addresses);
      
      if (provisionersError) {
        console.error('Error fetching provisioners data:', provisionersError);
      }

      // Then get the latest hourly stats for these addresses
      const { data: hourlyStats, error: hourlyError } = await supabase
        .from('hourly_node_stats')
        .select('address, total_reward_change, timestamp')
        .in('address', addresses)
        .gte('timestamp', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()) // Last 24 hours
        .order('timestamp', { ascending: false });
      
      if (hourlyError) {
        console.error('Error fetching hourly stats:', hourlyError);
        // Continue without hourly stats if the query fails
      }

      // Get 30-day average daily rewards for APY calculation
      const { data: dailyAverages, error: dailyError } = await supabase
        .from('daily_performance_stats')
        .select('address, daily_average')
        .in('address', addresses)
        .gte('date', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (dailyError) {
        console.error('Error fetching daily averages:', dailyError);
      }

      // Calculate 30-day average rewards for each address
      const thirtyDayAverages = new Map();
      dailyAverages?.forEach(stat => {
        if (!thirtyDayAverages.has(stat.address)) {
          thirtyDayAverages.set(stat.address, []);
        }
        thirtyDayAverages.get(stat.address).push(stat.daily_average || 0);
      });

      // Calculate average daily rewards over 30 days
      const avgDailyRewards = new Map();
      thirtyDayAverages.forEach((dailyRewards, address) => {
        const average = dailyRewards.reduce((sum, reward) => sum + reward, 0) / dailyRewards.length;
        avgDailyRewards.set(address, average);
      });

      // Get the most recent hourly reward for each address
      const latestHourlyRewards = new Map();
      hourlyStats?.forEach(stat => {
        if (!latestHourlyRewards.has(stat.address)) {
          latestHourlyRewards.set(stat.address, stat.total_reward_change || 0);
        }
      });

      // Create a map of provisioner data by address for easy lookup
      const provisionersMap = new Map();
      provisionersData?.forEach(p => {
        provisionersMap.set(p.address, p);
      });

      // Combine the data and calculate additional fields for sorting
      let combinedData = rankings?.map(staker => {
        const hourlyReward = latestHourlyRewards.get(staker.address) || 0;
        const networkPercentage = actualNetworkStake > 0 ? ((staker.stake || 0) / actualNetworkStake) * 100 : 0;
        const avgDaily = avgDailyRewards.get(staker.address) || 0;
        const apy = calculateAPY(avgDaily, staker.stake || 0);
        const stakeValue = (staker.stake || 0) * (duskPrice || 0);
        const hourlyUsd = hourlyReward * (duskPrice || 0);
        const provisionerData = provisionersMap.get(staker.address);
        
        return {
          ...staker,
          hourly_reward_amount: hourlyReward,
          network_percentage: networkPercentage,
          apy: apy,
          avg_daily_rewards: avgDaily,
          stake_value: stakeValue,
          hourly_usd: hourlyUsd,
          public_key: provisionerData?.public_key || null
        };
      }) || [];

      // Apply client-side sorting for fields not available in the database
      if (sortField === 'hourly_reward_amount' || sortField === 'network_percentage' || sortField === 'apy' || sortField === 'stake_value' || sortField === 'hourly_usd') {
        combinedData.sort((a, b) => {
          const aValue = a[sortField] || 0;
          const bValue = b[sortField] || 0;
          
          if (sortDirection === 'asc') {
            return aValue - bValue;
          } else {
            return bValue - aValue;
          }
        });
      }

      return combinedData;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: duskPrice } = useQuery({
    queryKey: ["duskPrice"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dusk_market_data')
        .select('current_price_usd')
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();
      
      if (error) throw error;
      return data?.current_price_usd || 0;
    },
    refetchInterval: 60000,
  });

  // Use the existing useNetworkStats hook instead of separate query
  const { data: networkStats } = useNetworkStats();
  
  const calculateAPY = (avgDailyRewards: number, stake: number) => {
    if (stake <= 0 || avgDailyRewards <= 0) return 0;
    // Annualized yield based on 30-day average daily rewards
    return (avgDailyRewards * 365 * 100) / stake;
  };
  
  const actualNetworkStake = networkStats?.totalStake || 0;

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-xl font-bold">All Active Stakers</CardTitle>
          <Trophy className="h-5 w-5 text-yellow-500" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 bg-gray-200 rounded animate-pulse" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const handleAddressClick = (address: string) => {
    setSelectedNode({
      id: address,
      address: address
    });
  };


  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc'); // Default to descending for new fields
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const SortableHeader = ({ field, children, className = "" }: { field: SortField; children: React.ReactNode; className?: string }) => (
    <TableHead 
      className={`cursor-pointer hover:bg-accent/50 transition-colors ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
        )}
      </div>
    </TableHead>
  );

  const currentPageTotalStake = allStakers?.reduce((sum, staker) => sum + (staker.stake || 0), 0) || 0;
  const totalHourlyRewards = allStakers?.reduce((sum, staker) => sum + (staker.hourly_reward_amount || 0), 0) || 0;

  const totalPages = Math.ceil((totalCount || 0) / stakersPerPage);
  const startRank = (currentPage - 1) * stakersPerPage + 1;

  // Generate pagination items
  const generatePaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <PaginationItem key="1">
          <PaginationLink onClick={() => setCurrentPage(1)}>1</PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        items.push(<PaginationEllipsis key="start-ellipsis" />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <PaginationItem key={page}>
          <PaginationLink 
            onClick={() => setCurrentPage(page)}
            isActive={currentPage === page}
          >
            {page}
          </PaginationLink>
        </PaginationItem>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<PaginationEllipsis key="end-ellipsis" />);
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => setCurrentPage(totalPages)}>{totalPages}</PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xl font-bold flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-500" />
          All Active Stakers
        </CardTitle>
        <div className="text-sm text-muted-foreground">
          Page {currentPage} of {totalPages} • {totalCount} total stakers
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-auto max-h-[600px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background">
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Address</TableHead>
                <TableHead className="text-center">Separated Key</TableHead>
                <SortableHeader field="stake" className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Coins className="h-4 w-4" />
                    Stake (DUSK)
                  </div>
                </SortableHeader>
                <SortableHeader field="stake_value" className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <DollarSign className="h-4 w-4" />
                    Stake Value
                  </div>
                </SortableHeader>
                <SortableHeader field="network_percentage" className="text-right">
                  Network %
                </SortableHeader>
                <SortableHeader field="hourly_reward_amount" className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <TrendingUp className="h-4 w-4" />
                    Hourly Rewards
                  </div>
                </SortableHeader>
                <SortableHeader field="hourly_usd" className="text-right">Hourly USD</SortableHeader>
                <SortableHeader field="apy" className="text-right">
                  APY %
                </SortableHeader>
                <SortableHeader field="total_rewards" className="text-right">
                  Total Earned
                </SortableHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allStakers?.map((staker, index) => {
                const stakeValue = staker.stake_value || 0;
                const hourlyRewardsValue = staker.hourly_usd || 0;
                const networkPercentage = staker.network_percentage || 0;
                const apy = staker.apy || 0;
                const globalRank = startRank + index;
                
                return (
                  <TableRow 
                    key={staker.address}
                    className="hover:bg-accent/50 transition-colors"
                  >
                    <TableCell className="font-medium text-center">
                      <div className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        globalRank <= 3 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {globalRank}
                      </div>
                    </TableCell>
                    <TableCell 
                      className="cursor-pointer hover:bg-accent/30 transition-colors"
                      onClick={() => handleAddressClick(staker.address)}
                    >
                      <span className="font-mono text-xs text-blue-600 hover:text-blue-800">
                        {staker.address.slice(0, 6)}...{staker.address.slice(-6)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        const publicKey = staker.public_key;
                        const hasSeparatedKey = publicKey && publicKey !== staker.address;
                        return (
                          <span className={`font-medium text-xs px-2 py-1 rounded ${
                            hasSeparatedKey ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'
                          }`}>
                            {hasSeparatedKey ? 'Yes' : 'No'}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatNumber(staker.stake || 0)}
                    </TableCell>
                    <TableCell className="text-right text-green-600 font-medium">
                      {formatCurrency(stakeValue)}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {formatPercentage(networkPercentage)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-blue-600 font-medium">
                        {formatNumber(staker.hourly_reward_amount || 0)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-green-600">
                      {formatCurrency(hourlyRewardsValue)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-medium ${
                        apy > 30 ? 'text-green-600' : apy > 20 ? 'text-yellow-600' : 'text-gray-600'
                      }`}>
                        {formatPercentage(apy)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {formatNumber(staker.total_rewards || 0)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                {generatePaginationItems()}
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
        
        {allStakers && allStakers.length > 0 && (
          <div className="mt-4 pt-4 border-t space-y-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className="font-semibold text-lg">
                  {formatNumber(currentPageTotalStake)}
                </div>
                <div className="text-muted-foreground">Current Page Stake</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-lg text-green-600">
                  {formatCurrency(currentPageTotalStake * (duskPrice || 0))}
                </div>
                <div className="text-muted-foreground">USD Value</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-lg text-blue-600">
                  {formatNumber(totalHourlyRewards)}
                </div>
                <div className="text-muted-foreground">Hourly Rewards</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-lg">
                  ${(duskPrice || 0).toFixed(4)}
                </div>
                <div className="text-muted-foreground">DUSK Price</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
